import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { renderPlatformOptions } from '../../../data/platforms';

const EditWebsiteModal = ({ isOpen, onClose, onSave, website, isSubmitting }) => {
  const [formData, setFormData] = useState({
    name: '',
    root_url: '',
    platform: '',
    description: ''
  });
  const [errors, setErrors] = useState({});

  // Populate form when website data changes
  useEffect(() => {
    if (website) {
      setFormData({
        name: website.name || '',
        root_url: website.root_url || '',
        platform: website.platform || '',
        description: website.description || ''
      });
    }
  }, [website]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`Edit modal - Field changed: ${name}, value: ${value}`); // Debug log
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for the field being edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Website name is required';
    }
    
    if (!formData.platform || !formData.platform.trim()) {
      newErrors.platform = 'Platform is required';
      console.log('Platform validation failed:', formData.platform); // Debug log
    }
    
    console.log('Edit form validation results:', { formData, newErrors }); // Debug log
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Create a new object without the root_url to prevent it from being updated
      const { root_url, ...updateData } = formData;
      onSave(website.id, updateData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto animate-fadeInUp">
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-start justify-between">
          <h2 id="modal-title" className="text-xl font-bold text-gray-900">Edit Website</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full p-1"
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="p-5 space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Website Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`bg-gray-50 border ${errors.name ? 'border-red-500' : 'border-gray-300'} text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5`}
                placeholder="My Website"
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>
            
            {/* URL - Made non-editable */}
            <div>
              <label htmlFor="root_url" className="block text-sm font-medium text-gray-700 mb-1">
                Website URL <span className="text-xs text-gray-500">(cannot be changed)</span>
              </label>
              <input
                type="text"
                id="root_url"
                name="root_url"
                value={formData.root_url}
                readOnly
                className="bg-gray-100 border border-gray-300 text-gray-500 text-sm rounded-lg block w-full p-2.5 cursor-not-allowed"
                aria-describedby="url-description"
              />
              <p id="url-description" className="mt-1 text-xs text-gray-500">To change URL, please delete this website and add a new one.</p>
            </div>
            
            {/* Platform */}
            <div>
              <label htmlFor="platform" className="block text-sm font-medium text-gray-700 mb-1">
                Platform *
              </label>
              <select
                id="platform"
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                className={`bg-gray-50 border ${errors.platform ? 'border-red-500' : 'border-gray-300'} text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5`}
              >
                {renderPlatformOptions()}
              </select>
              {errors.platform && <p className="mt-1 text-sm text-red-600">{errors.platform}</p>}
            </div>
            
            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                placeholder="Brief description of your website"
              />
            </div>

            <p className="text-sm text-gray-500">* Required fields</p>
          </div>
          
          {/* Footer */}
          <div className="p-5 border-t border-gray-100 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 font-medium text-sm rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white font-medium text-sm rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : "Update Website"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditWebsiteModal;